// Central source of truth for uploadable/displayable file types.
//
// Why this exists: acceptedFileTypes (what FilePond lets you pick),
// the "supported" hint text shown next to upload widgets, and Profile.vue's
// isPdf()/isDirectVideoFile() display logic used to be three separately
// hardcoded lists. They drifted apart before (e.g. a MIME type accepted for
// upload that Profile.vue had no rendering path for). Every upload widget
// and every display check should derive from this file instead of
// maintaining its own list.
//
// To add a new file type to a widget: add/edit a group below, then
// reference it in the relevant UPLOAD_CONTEXTS entry. Don't hardcode
// mime types or extensions anywhere else.

const IMAGE = {
  mimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  extensions: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
  label: 'JPG, PNG, GIF, WEBP images',
  maxFileSizeMB: 10
};

const VIDEO = {
  mimeTypes: ['video/mp4', 'video/quicktime', 'video/x-m4v', 'video/webm', 'video/ogg'],
  extensions: ['mp4', 'mov', 'm4v', 'webm', 'ogg'],
  label: 'MP4, MOV, WEBM, OGG videos',
  maxFileSizeMB: 100
};

const PDF = {
  mimeTypes: ['application/pdf'],
  extensions: ['pdf'],
  label: 'PDF documents',
  maxFileSizeMB: 15
};

const DOCUMENT = {
  mimeTypes: [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain'
  ],
  extensions: ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'],
  label: 'Word, Excel, PowerPoint, and text documents',
  maxFileSizeMB: 25
};

const AUDIO = {
  mimeTypes: ['audio/mpeg', 'audio/wav', 'audio/mp4', 'audio/x-m4a'],
  extensions: ['mp3', 'wav', 'm4a'],
  label: 'MP3, WAV, M4A audio',
  maxFileSizeMB: 50
};

const ARCHIVE = {
  mimeTypes: ['application/zip', 'application/x-zip-compressed'],
  extensions: ['zip'],
  label: 'ZIP archives',
  maxFileSizeMB: 50
};

// How many files can be added in a single drop/selection action. This is a
// browser/UX protection (prevents someone dragging in hundreds of files at
// once and hanging the tab) and applies uniformly regardless of context —
// it's independent of maxTotalFiles below, which caps the persistent total.
export const MAX_FILES_PER_UPLOAD = 10;

// Each key is an upload "context" — one specific widget/use case.
// Add new widgets here rather than inlining acceptedFileTypes elsewhere.
// maxTotalFiles is the ceiling on how many files can exist for that context
// at once (existing + newly added). Leave it undefined for contexts that
// aren't a persistent multi-file gallery (e.g. a single avatar upload).
export const UPLOAD_CONTEXTS = {
  // Media Library (Library.vue) — photos and directly-uploaded video files.
  // Profile.vue's mediaItems section renders both via isVideo()/videoThumbnail().
  library: {
    groups: [IMAGE, VIDEO],
    maxTotalFiles: 50
  },
  // Proof of Certification (Proof.vue) — Profile.vue's certification-files
  // section only ever renders isPdf() or a generic image fallback; it has
  // no video handling, so video must never be added to this context.
  proof: {
    groups: [IMAGE, PDF],
    maxTotalFiles: 10
  },
  // Cover photo + profile avatar (EditProfileInfo.vue). Rendered as plain
  // <img>/<v-img> tags — no PDF or video handling anywhere in that path.
  // Not a multi-file gallery (one avatar, one cover photo), so no
  // maxTotalFiles here — only the size cap applies.
  profilePhoto: {
    groups: [IMAGE]
  },
  // Chat image attachments (Chat.vue). Rendered as a <v-img> thumbnail for
  // images; other common file types (PDF, docs, audio, video, archives) are
  // rendered as an icon + filename chip that opens the file in a new tab —
  // see getFileKind() below, used by Chat.vue to pick the right rendering.
  // One file per message, not a gallery — only the size cap applies.
  chat: {
    groups: [IMAGE, VIDEO, PDF, DOCUMENT, AUDIO, ARCHIVE]
  }
};

function groupsFor(contextName) {
  const context = UPLOAD_CONTEXTS[contextName];
  if (!context) {
    console.warn(`uploadFileTypes: unknown upload context "${contextName}"`);
    return [];
  }
  return context.groups;
}

// FilePond's acceptedFileTypes prop wants a comma-separated MIME type string.
export function getAcceptedFileTypes(contextName) {
  return groupsFor(contextName)
    .flatMap(group => group.mimeTypes)
    .join(', ');
}

// Ceiling on total files for a context (existing + new). Returns undefined
// for contexts that aren't a persistent multi-file gallery — callers should
// only bind FilePond's :maxFiles prop when this is defined.
export function getMaxTotalFiles(contextName) {
  const context = UPLOAD_CONTEXTS[contextName];
  return context ? context.maxTotalFiles : undefined;
}

// Largest per-file size limit among a context's groups, e.g. a context
// mixing images (10MB) and video (100MB) reports 100MB — FilePond only
// takes one maxFileSize value, so it has to be the ceiling that
// accommodates the largest allowed group.
function contextMaxFileSizeMB(contextName) {
  const groups = groupsFor(contextName);
  if (!groups.length) return 0;
  return Math.max(...groups.map(group => group.maxFileSizeMB));
}

// FilePond's maxFileSize prop wants a string like "10MB".
export function getMaxFileSizeLabel(contextName) {
  return contextMaxFileSizeMB(contextName) + 'MB';
}

// For upload flows using a plain <input type="file"> (no FilePond) — those
// have no built-in size-cap mechanism, so callers check this manually
// against the picked File's .size before uploading.
export function getMaxFileSizeBytes(contextName) {
  return contextMaxFileSizeMB(contextName) * 1024 * 1024;
}

// Human-readable caption for display next to the upload widget, including
// size/count limits alongside supported types.
export function getUploadHintText(contextName) {
  const context = UPLOAD_CONTEXTS[contextName];
  if (!context) return '';
  const labels = context.groups.map(group => group.label);
  if (!labels.length) return '';

  let text = 'Supported: ' + labels.join(' and ') + `. Up to ${contextMaxFileSizeMB(contextName)}MB per file`;
  if (context.maxTotalFiles) {
    text += `, ${context.maxTotalFiles} files total`;
  }
  text += '.';
  return text;
}

function hasExtension(fileNameOrUrl, extensions) {
  const name = (fileNameOrUrl || '').toLowerCase();
  return extensions.some(ext => name.endsWith('.' + ext));
}

// Used by Profile.vue in place of its own hardcoded regex/extension checks,
// so "what's accepted for upload" and "what Profile.vue can display" can
// never silently diverge.
export function isVideoFileName(fileNameOrUrl) {
  return hasExtension(fileNameOrUrl, VIDEO.extensions);
}

export function isPdfFileName(fileNameOrUrl) {
  return hasExtension(fileNameOrUrl, PDF.extensions);
}

export function isImageFileName(fileNameOrUrl) {
  return hasExtension(fileNameOrUrl, IMAGE.extensions);
}

export function isAudioFileName(fileNameOrUrl) {
  return hasExtension(fileNameOrUrl, AUDIO.extensions);
}

export function isArchiveFileName(fileNameOrUrl) {
  return hasExtension(fileNameOrUrl, ARCHIVE.extensions);
}

export function isDocumentFileName(fileNameOrUrl) {
  return hasExtension(fileNameOrUrl, DOCUMENT.extensions);
}

// One-stop classification used by Chat.vue to decide how to render an
// attachment: an inline image preview vs. an icon + filename chip that opens
// the file in a new tab. Returns 'image' | 'video' | 'pdf' | 'document' |
// 'audio' | 'archive' | 'other'.
export function getFileKind(fileNameOrUrl) {
  if (isImageFileName(fileNameOrUrl)) return 'image';
  if (isVideoFileName(fileNameOrUrl)) return 'video';
  if (isPdfFileName(fileNameOrUrl)) return 'pdf';
  if (isAudioFileName(fileNameOrUrl)) return 'audio';
  if (isArchiveFileName(fileNameOrUrl)) return 'archive';
  if (isDocumentFileName(fileNameOrUrl)) return 'document';
  return 'other';
}
